/**
 * Modul-Registry der Portal-Shell (einzige Quelle für Modulschlüssel, Bereiche und Pfade).
 *
 * Navigation laut DESIGN.md § 11/5.1: Arbeitsplatz · Kundenportal · CRM · Warenwirtschaft · Marketing · Verwaltung
 * (Abweichung #819, Matthias 03.11.2026: Bereich „Kunden“ heißt „Kundenportal“, CRM ist ein eigener Bereich unter /crm;
 * Abweichung #820, Matthias 03.11.2026: Bereich `lager` heißt „Warenwirtschaft“, Bereich `system` heißt „Verwaltung“,
 * Modul `verwaltung` liegt unter /verwaltung — Schlüssel, Gruppen `mod-*`, Rechte und API unverändert).
 * Sichtbarkeit entscheidet ausschließlich der PDP (Cerbos, Ressource `portal_module`, Aktion `view`);
 * die Policy liegt in brueckner-portal/portal-infra `portal/cerbos/policies/portal_module.yaml`
 * und verwendet dieselben Schlüssel. Freischaltung je Modul: Tabelle `core.module` (enabled).
 */

import type { PortalRole } from './api.js';

/** Programmname des Moduls `loyalty` (Matthias 04.11.2026, #854 Nachtrag) — verbindlich, genau so geschrieben */
export const LOYALTY_PROGRAMM = 'House of Brückner - Royalty';
/** Kurzform für den Kopf der Navigation (#854: volle Form bricht ab 1100 px um; Seitentitel/Kachel tragen die volle Form) */
export const LOYALTY_KURZ = 'Royalty';

export const AREAS = [
  { key: 'arbeitsplatz', label: 'Arbeitsplatz' },
  { key: 'kunden', label: 'Kundenportal' },
  { key: 'crm', label: 'CRM' },
  // #854: Loyalty (Backend-Verwaltung des Treueprogramms „House of Brückner - Royalty“, Team-Sicht) ist ein eigener Bereich
  // unter /loyalty — wie CRM (#819). Kopf-Navigation: Kurzform „Royalty“ (volle Form bricht ab 1100 px um)
  { key: 'loyalty', label: LOYALTY_KURZ },
  { key: 'lager', label: 'Warenwirtschaft' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'system', label: 'Verwaltung' },
] as const;

export type AreaKey = (typeof AREAS)[number]['key'];

export interface ModuleDefinition {
  /** Modulschlüssel = Cerbos-Ressourcen-ID */
  key: string;
  area: AreaKey;
  label: string;
  /** Pfad im Admin-Frontend */
  path: string;
  /** Scope-Präfixe (`modul.ressource.aktion`), über die ein Agent das Modul sehen darf */
  scopePrefixes: readonly string[];
  /** Freischaltung beim ersten Anlegen in `core.module` */
  defaultEnabled: boolean;
  /**
   * Stufen, die per Gruppe `mod-<key>-<stufe>` vergeben werden (ADR 003 Variante G, Modul „Verwaltung");
   * leer = Modul ohne Freischaltung (Arbeitsplatz: jede Person mit Rolle).
   */
  stufen: readonly Stufe[];
}

/** Stufen je Modul (ADR 003 § 3): lesen < bearbeiten < verwalten — höhere Stufe schließt niedrigere ein */
export const STUFEN = ['lesen', 'bearbeiten', 'verwalten'] as const;
export type Stufe = (typeof STUFEN)[number];
export const STUFE_RANG: Record<Stufe, number> = { lesen: 1, bearbeiten: 2, verwalten: 3 };
const ALLE_STUFEN = STUFEN;

export const MODULES = [
  { key: 'arbeitsplatz', area: 'arbeitsplatz', label: 'Übersicht', path: '/', scopePrefixes: [], defaultEnabled: true, stufen: [] },
  { key: 'kunden', area: 'kunden', label: 'Kundenportal', path: '/kunden', scopePrefixes: ['customer'], defaultEnabled: true, stufen: ALLE_STUFEN },
  // #815 CRM Phase 1: leeres Modul (Kern-Kundenstamm Schema kunden, nur Liste lesen); Sichtbarkeit nur per Freischaltung mod-crm-*
  // #819: eigener Bereich crm, Route /crm (alte /kunden/crm → 301 im Caddyfile); Schlüssel, Gruppen, Rechte und API unverändert
  { key: 'crm', area: 'crm', label: 'CRM', path: '/crm', scopePrefixes: ['crm'], defaultEnabled: true, stufen: ALLE_STUFEN },
  // #854 Loyalty Phase 1: leeres Modul (nur Reiter + Berechtigungsstruktur, keine Daten/kein Schema); eigener Bereich loyalty,
  // Route /loyalty; Sichtbarkeit nur per Freischaltung mod-loyalty-* (ADR 005). Kundensicht kommt später getrennt im Kundenportal.
  // Label = Programmname (Unternavigation, Verwaltungs-Kachel, Modul-Gate); technische Schlüssel bleiben `loyalty`.
  { key: 'loyalty', area: 'loyalty', label: LOYALTY_PROGRAMM, path: '/loyalty', scopePrefixes: ['loyalty'], defaultEnabled: true, stufen: ALLE_STUFEN },
  { key: 'community', area: 'kunden', label: 'Community', path: '/kunden/community', scopePrefixes: ['community'], defaultEnabled: false, stufen: ALLE_STUFEN },
  { key: 'lager', area: 'lager', label: 'Warenwirtschaft', path: '/lager', scopePrefixes: ['warehouse', 'catalog'], defaultEnabled: true, stufen: ALLE_STUFEN },
  { key: 'marketing', area: 'marketing', label: 'Marketing-Hub', path: '/marketing', scopePrefixes: ['marketing'], defaultEnabled: true, stufen: ALLE_STUFEN },
  { key: 'benachrichtigungen', area: 'marketing', label: 'Benachrichtigungen', path: '/marketing/benachrichtigungen', scopePrefixes: ['notify'], defaultEnabled: false, stufen: ALLE_STUFEN },
  // #820: erstes Modul im Bereich „Verwaltung“ und Route /verwaltung (alte /system/verwaltung → 301 im Caddyfile): der Kopf-Link
  // führt Berechtigte (mod-verwaltung-*) auf /verwaltung, ohne Verwaltungsrecht weiter auf /system (Verbindungen)
  { key: 'verwaltung', area: 'system', label: 'Verwaltung', path: '/verwaltung', scopePrefixes: ['admin'], defaultEnabled: true, stufen: ['lesen', 'verwalten'] },
  { key: 'verbindungen', area: 'system', label: 'Verbindungen', path: '/system', scopePrefixes: ['integration', 'sync'], defaultEnabled: true, stufen: ALLE_STUFEN },
  { key: 'apps', area: 'system', label: 'Apps', path: '/system/apps', scopePrefixes: [], defaultEnabled: false, stufen: ALLE_STUFEN },
] as const satisfies readonly ModuleDefinition[];

export type ModuleKey = (typeof MODULES)[number]['key'];

export const MODULE_KEYS: readonly ModuleKey[] = MODULES.map((m) => m.key);

export function isModuleKey(value: string): value is ModuleKey {
  return (MODULE_KEYS as readonly string[]).includes(value);
}

export function getModule(key: ModuleKey): ModuleDefinition {
  const m = MODULES.find((x) => x.key === key);
  if (!m) throw new Error(`Unbekanntes Modul: ${key}`);
  return m;
}

/** Authentik-Gruppe einer Freischaltung (ADR 003 § 3.1): `mod-<modul>-<stufe>` */
export function modulGruppe(key: string, stufe: Stufe): string {
  return `mod-${key}-${stufe}`;
}

/** Freigeschaltete Module einer Person: Modulschlüssel → höchste Stufe (Claim `portal_modules`) */
export type ModulFreischaltung = Partial<Record<ModuleKey, Stufe>>;

/**
 * #898 (Entscheidung Matthias 05.11.2026, ersetzt ADR 003 § Superuser): Superuser (Authentik `is_superuser`, Claim
 * `portal_superuser`) hat in JEDEM Modul der Registry — bestehend und künftig — automatisch die höchste Stufe `verwalten`,
 * unabhängig von `mod-*`-Gruppen. Einzige Stelle: Principal-Builder der API (`principalFromSession`).
 */
export function vollzugriff(): ModulFreischaltung {
  const out: ModulFreischaltung = {};
  for (const m of MODULES) if (m.stufen.length) out[m.key] = m.stufen[m.stufen.length - 1]!;
  return out;
}

/**
 * Claim `portal_modules` (Scope `portal`, Blueprint 50) → Freischaltung. Allowlist: nur Module der Registry mit
 * der jeweiligen Stufe; alles andere wird verworfen (nie aus Request-Daten).
 */
export function freischaltungAusClaim(value: unknown): ModulFreischaltung {
  const out: ModulFreischaltung = {};
  if (!value || typeof value !== 'object' || Array.isArray(value)) return out;
  for (const [key, stufe] of Object.entries(value as Record<string, unknown>)) {
    const m = MODULES.find((x) => x.key === key);
    if (m && typeof stufe === 'string' && (m.stufen as readonly string[]).includes(stufe)) out[m.key] = stufe as Stufe;
  }
  return out;
}

/**
 * Vorbelegung je Rolle (ADR 003 § 4, auf die Registry-Schlüssel abgebildet) — nur Vorschlag beim Einladen,
 * je Person per Klick übersteuerbar. `verwaltung:verwalten` ist ausschließlich für `gf` vorbelegt (#811, Explizitheit).
 * #854: `loyalty` nur für gf (verwalten), office (bearbeiten), ops (lesen) vorbelegt.
 */
export const MODUL_VORBELEGUNG: Record<PortalRole, ModulFreischaltung> = {
  gf: {
    verwaltung: 'verwalten', kunden: 'verwalten', crm: 'verwalten', loyalty: 'verwalten', community: 'verwalten', lager: 'verwalten',
    marketing: 'verwalten', benachrichtigungen: 'verwalten', verbindungen: 'verwalten', apps: 'verwalten',
  },
  office: { kunden: 'bearbeiten', crm: 'bearbeiten', loyalty: 'bearbeiten', community: 'lesen', lager: 'lesen', marketing: 'lesen' },
  marketing: { community: 'bearbeiten', lager: 'lesen', marketing: 'verwalten', benachrichtigungen: 'bearbeiten' },
  lager: { lager: 'bearbeiten' },
  lager_lead: { lager: 'verwalten' },
  ops: {
    verwaltung: 'lesen', kunden: 'lesen', crm: 'lesen', loyalty: 'lesen', community: 'lesen', lager: 'lesen', marketing: 'lesen',
    benachrichtigungen: 'lesen', verbindungen: 'lesen', apps: 'lesen',
  },
  readonly: { community: 'lesen', lager: 'lesen', marketing: 'lesen' },
  moderator: { community: 'verwalten' },
};
